import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import apiUrls from "../../../api";
import type {
  GazeHeatmapDataResponse,
  SceneGazeHeatmapDataResponse,
  SceneGazeData,
} from "../types/gazeHeatmapTypes";

interface UseGazeHeatmapDataParams {
  sessionId?: number;
  contentId?: number;
  enabled?: boolean;
}

interface UseGazeHeatmapDataReturn {
  data: GazeHeatmapDataResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

interface UseSceneGazeHeatmapDataParams {
  contentId: number;
  sessionIds?: number[];
  groupMemberIds?: (number | string)[];
  enabled?: boolean;
}

interface UseSceneGazeHeatmapDataReturn {
  data: SceneGazeHeatmapDataResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch gaze heatmap data for a single session
 */
export function useGazeHeatmapData({
  sessionId,
  contentId,
  enabled = true,
}: UseGazeHeatmapDataParams): UseGazeHeatmapDataReturn {
  const [data, setData] = useState<GazeHeatmapDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetchedRef = useRef<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled || !sessionId) return;

    const cacheKey = `${sessionId}-${contentId || "all"}`;
    if (fetchedRef.current === cacheKey && data) return;

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append("sessionId", String(sessionId));
      if (contentId) params.append("contentId", String(contentId));

      const response = await axios.get<GazeHeatmapDataResponse>(
        `${apiUrls.stats.gazeHeatmapData}?${params.toString()}`,
      );
      setData(response.data);
      fetchedRef.current = cacheKey;
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch gaze heatmap data"));
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, contentId, enabled, data]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchedRef.current = null;
    void fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch };
}

/**
 * Hook to fetch aggregated gaze heatmap data for a scene across multiple sessions
 */
export function useSceneGazeHeatmapData({
  contentId,
  sessionIds,
  groupMemberIds,
  enabled = true,
}: UseSceneGazeHeatmapDataParams): UseSceneGazeHeatmapDataReturn {
  const [data, setData] = useState<SceneGazeHeatmapDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetchedRef = useRef<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled || !contentId) return;

    const cacheKey = `${contentId}-${sessionIds?.join(",") || ""}-${
      groupMemberIds?.join(",") || ""
    }`;
    if (fetchedRef.current === cacheKey && data) return;

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append("contentId", String(contentId));

      if (sessionIds && sessionIds.length > 0) {
        params.append("sessionIds", sessionIds.join(","));
      }

      if (groupMemberIds && groupMemberIds.length > 0) {
        params.append("groupMemberIds", groupMemberIds.join(","));
      }

      const response = await axios.get<SceneGazeHeatmapDataResponse>(
        `${apiUrls.stats.sceneGazeHeatmapData}?${params.toString()}`,
      );
      setData(response.data);
      fetchedRef.current = cacheKey;
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to fetch scene gaze heatmap data"));
    } finally {
      setIsLoading(false);
    }
  }, [contentId, sessionIds, groupMemberIds, enabled, data]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    fetchedRef.current = null;
    void fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch };
}

/**
 * Helper function to check if a scene has 360° media (not 2D or other)
 */
export function sceneHas360Media(scene: SceneGazeData): boolean {
  return !!(scene.media && scene.media.media360Tag === "media360");
}

/**
 * Helper function to filter scenes with 360° media
 */
export function filterScenesWithMedia(scenes: SceneGazeData[]): SceneGazeData[] {
  return scenes.filter(sceneHas360Media);
}
